import { ComponentType, lazy } from 'react';

/**
 * Session storage key to track if a refresh has already been attempted
 */
const REFRESH_KEY = 'chunk_load_retry_attempted';

/**
 * Wraps a dynamic import with ChunkLoadError handling.
 * If a chunk fails to load (e.g., after a deployment), this will:
 * 1. Check if a refresh has already been attempted (via sessionStorage)
 * 2. If not, set the flag and reload the page once
 * 3. If already attempted, throw the error (prevents infinite loops)
 * 
 * @param importFunction - The dynamic import function (e.g., () => import('./Component'))
 * @returns A lazy-loaded React component
 */
export function lazyLoadWithRetry<T extends ComponentType<any>>(
  importFunction: () => Promise<{ default: T }>
): React.LazyExoticComponent<T> {
  return lazy(() => {
    return importFunction().catch((error: Error) => {
      // Check if this is a ChunkLoadError
      const isChunkLoadError = 
        error.name === 'ChunkLoadError' || 
        /Loading chunk [\d]+ failed/.test(error.message) ||
        /Failed to fetch dynamically imported module/.test(error.message);

      if (isChunkLoadError) {
        const hasRefreshed = sessionStorage.getItem(REFRESH_KEY);
        
        if (!hasRefreshed) {
          // Set the flag before refreshing to prevent infinite loops
          sessionStorage.setItem(REFRESH_KEY, 'true');
          
          // Reload the page to get the latest chunks
          window.location.reload();
          
          // Return a never-resolving promise to prevent rendering
          // during the reload process
          return new Promise(() => {});
        }
        
        // If we've already refreshed once, clear the flag and throw
        // This allows future deployments to trigger another refresh
        sessionStorage.removeItem(REFRESH_KEY);
      }
      
      // Re-throw the error for other error types or if refresh already attempted
      throw error;
    });
  });
}

/**
 * Clears the refresh retry flag from sessionStorage.
 * Useful for testing or manual reset scenarios.
 */
export function clearRetryFlag(): void {
  sessionStorage.removeItem(REFRESH_KEY);
}
